import { HasLanguageProficiencyCodes, LanguageProficiencyMetaData, TestType, UploadStatus } from '@applyboard/school-applications-types-lib'
import { GetApplicationPagePropsResponse, PersonalInformationSpecificFields, SchoolInformationSpecificFields } from '@backend/agents-ui-api/routes/page-props/GET/types'
import { z, ZodTypeAny } from 'zod'
import { StudentApplication } from '../components/Application/types'
import { Student } from '../hooks/useGetStudent'
import { getLanguageProficiencyFieldsInfo } from './languageProficiencyUtils'
import { get } from 'lodash'
import { DocumentTags } from './enums'

const camelToLabel = (s: string) => s.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase())

export enum LanguageProficiencyTypeKeys {
  IELTS = 'ielts',
  PTE = 'pte',
  TOEFL = 'toefl',
  OTHER = 'other',
  DUOLINGO = 'duolingo',
  CAEL = 'cael',
  CAMBRIDGE = 'cambridge',
  MET = 'met',
  EPP = 'epp',
  CANTEST = 'canTest',
  LANGUAGECERT_ESOL = 'languageCertEsol',
  LEAP_4_ADVANCED = 'leap4Advanced',
  
}

enum AdditionalDocumentTypeKeys {
  STATEMENT_OF_PURPOSE = 'statementOfPurpose',
  LETTER_OF_RECOMMENDATION = 'letterOfRecommendation',
  CONSENT_FORM = 'consentForm',
  GMAT_SCORE = 'gmatScore',
  GRE_SCORE = 'greScore',
  RESUME = 'resume',
  EMPLOYMENT_LETTER = 'employmentLetter',
  VOLUNTEER_LETTER = 'volunteerLetter',
  BIRTH_CERTIFICATE = 'birthCertificate',
  WES_EVALUATION_REPORT = 'wesEvaluationReport',
  PORTFOLIO = 'portfolio',
  FINANCIAL_PROOF = 'financialProof',
  IQAS_ASSESSMENT = 'iqasAssessment',
  CASPER_TEST_SCORE_REPORT = 'casperTestScoreReport',
  SNAPSHOT = 'snapshot',
  PROOF_OF_NAME_CHANGE = 'proofOfNameChange',
  RELEASE_OF_INFORMATION_FORM = 'releaseOfInformationForm',
  AUTHORIZED_RP_RELEASE_OF_INFORMATION_FORM = 'authorizedRpReleaseOfInformationForm',
  NURSING_LICENSE = 'nursingLicense',
}

type Fields<T> = Record<keyof T, { enabled: boolean; required: boolean }>
export type SpecificFields = GetApplicationPagePropsResponse['data']['specificFields']

export type SpecificFieldValidationErrorField = {
  field: string
  label: string
  message: string
}

export type ValidationResult = {
  isRequiredSpecificFieldsValid: boolean
  requiredSpecificFieldsErrors: SpecificFieldValidationErrorField[]
}

function isSpecificFieldRequired(pageProps: SpecificFields, fieldPath: string): boolean {
  return get(pageProps, `${fieldPath}.required`) ?? false
}

function getRequiredLanguageProficiencyProofTypes(pageProps: SpecificFields): DocumentTags[] {
  return Object.entries(LanguageProficiencyTypeKeys).filter(([, field]) => {
    return isSpecificFieldRequired(pageProps, `languageProficiency.proofTypes.${field}`)
  }).map(([documentType]) => {
    const fieldsInfo = getLanguageProficiencyFieldsInfo(documentType)
    return fieldsInfo?.fileType
  }).filter(type => type) as DocumentTags[]
}

function getOtherRequiredDocumentTypes(pageProps: SpecificFields): DocumentTags[] {
  return Object.entries(AdditionalDocumentTypeKeys).filter(([, field]) => {
    return isSpecificFieldRequired(pageProps, `otherRequiredDocuments.documentTypes.${field}`)
  }).map(([documentType]) => documentType as DocumentTags)
}

export function specificFieldValidations(pageProps: SpecificFields) {
  return {
    isSpecificFieldRequired: (fieldPath: string) => isSpecificFieldRequired(pageProps, fieldPath),
    getRequiredLanguageProficiencyProofTypes: () => getRequiredLanguageProficiencyProofTypes(pageProps),
    getOtherRequiredDocumentTypes: () => getOtherRequiredDocumentTypes(pageProps),
  }
}

// create function to get the document processing config passport enabled
export const getDocumentProcessingConfigPassportEnabled = (pageProps: GetApplicationPagePropsResponse['data']['documentProcessingConfig']) => {
  return pageProps?.documentTypes?.PASSPORT?.enabled ?? false
}

const createPersonalInfoSchema = (config: PersonalInformationSpecificFields) =>
  z.object(
    Object.entries(config)
      .filter(([_, f]) => f.enabled && f.required)
      .reduce(
        (acc, [k]) => {
          acc[k] = z.string().min(1)
          return acc
        },
        {} as Record<string, ZodTypeAny>,
      ),
  )

const createSchoolInfoSchema = (config: SchoolInformationSpecificFields) =>
  z.object(
    Object.entries(config)
      .filter(([_, f]) => f.enabled && f.required)
      .reduce(
        (acc, [k]) => {
          if (k === 'haveAttendedSchool') {
            acc[k] = z.boolean()
          } else if (k === 'schoolAssignedStudentId') {
            acc[k] = z.string().min(1).max(15).regex(/^(?! )([\w -]*)(?<! )$/)
          } else if (k === 'ontarioEducationNumber' || k === 'albertaStudentNumber' || k === 'personalEducationNumber') {
            acc[k] = z.number().min(100000000).max(999999999)
          } else {
            acc[k] = z.number()
          }
          return acc
        },
        {} as Record<string, ZodTypeAny>,
      ),
  )

const validateStudentAttributes = (
  studentAttributes: Student['attributes'],
  specificFields: SpecificFields,
  errors: SpecificFieldValidationErrorField[],
): boolean => {
  if (!specificFields?.personalInformation || !specificFields?.schoolInformation) return true

  const personalInfoSchema = createPersonalInfoSchema(
    specificFields.personalInformation as Fields<PersonalInformationSpecificFields>,
  )
  const schoolInfoSchema = createSchoolInfoSchema(
    specificFields.schoolInformation as Fields<SchoolInformationSpecificFields>,
  )

  const schema = z.object({
    personalInformation: personalInfoSchema,
    schoolInformation: schoolInfoSchema,
  })

  const result = schema.safeParse(studentAttributes)
  if (result.success) return true

  result.error.errors.forEach(err => {
    const path = err.path.join('.')
    const key = err.path.pop() as string
    const label = camelToLabel(key)
    errors.push({
      field: path,
      label: `${path.startsWith('personalInformation') ? 'Personal Information' : 'School Information'} - ${label}`,
      message: err.message,
    })
  })

  return false
}

const validateLanguageProficiency = (
  application: StudentApplication,
  specificFields: SpecificFields,
  errors: SpecificFieldValidationErrorField[],
): boolean => {
  if (!specificFields?.languageProficiency?.proofTypes || !application?.attributes) return true

  const requiredProofTypes = Object.entries(specificFields.languageProficiency.proofTypes).filter(
    ([_, settings]) => settings.enabled && settings.required,
  )

  if (requiredProofTypes.length === 0) return true

  const languageProficiency = application.attributes.languageProficiency
  const providedTypes =
    languageProficiency?.hasLanguageProficiency === HasLanguageProficiencyCodes.HAVE &&
    languageProficiency?.languageProficiencyData
      ? Object.values(languageProficiency.languageProficiencyData)
          .map(data => (data as LanguageProficiencyMetaData)?.testType)
          .filter(Boolean)
      : []

  let isValid = true

  requiredProofTypes.forEach(([proofType, _]) => {
    if (!providedTypes.includes(proofType.toUpperCase() as TestType)) {
      isValid = false
      const label = camelToLabel(proofType)
      errors.push({
        field: `languageProficiency.${proofType}`,
        label: `Language Proficiency - ${label} Test Proof`,
        message: `${label} is required`,
      })
    }
  })

  return isValid
}

const validateOtherDocuments = (
  application: StudentApplication,
  specificFields: SpecificFields,
  errors: SpecificFieldValidationErrorField[],
): boolean => {
  if (!specificFields?.otherRequiredDocuments?.documentTypes || !application?.attributes) return true

  const files = application.attributes.files ?? {}
  let isValid = true

  const requiredOtherDocTypes = Object.entries(specificFields.otherRequiredDocuments.documentTypes).filter(
    ([_, settings]) => settings.enabled && settings.required,
  )

  if (requiredOtherDocTypes.length === 0) return true

  requiredOtherDocTypes.forEach(([docType, _]) => {
    const documentTagKey = Object.entries(AdditionalDocumentTypeKeys).find(
      ([_, value]) => value === docType,
    )?.[0]

    const hasDocument = Object.values(files).some(
      file => file && file.type === documentTagKey && file.uploadStatus === UploadStatus.UPLOADED,
    )

    if (!hasDocument) {
      isValid = false
      const label = camelToLabel(docType)
      errors.push({
        field: `otherRequiredDocuments.${docType}`,
        label: `Other Required Documents - ${label}`,
        message: `${label} is required`,
      })
    }
  })

  return isValid
}

export const validateRequiredSpecificFields = (
  specificFields: SpecificFields,
  student?: Student,
  application?: StudentApplication,
): ValidationResult => {
  if (!specificFields || !student?.attributes) {
    return {
      isRequiredSpecificFieldsValid: true,
      requiredSpecificFieldsErrors: [],
    }
  }

  const errors: SpecificFieldValidationErrorField[] = []

  const studentValid = validateStudentAttributes(student.attributes, specificFields, errors)
  const langValid = validateLanguageProficiency(
    application ?? ({} as StudentApplication),
    specificFields,
    errors,
  )
  const otherDocsValid = validateOtherDocuments(
    application ?? ({} as StudentApplication),
    specificFields,
    errors,
  )

  const isValid = studentValid && langValid && otherDocsValid

  return {
    isRequiredSpecificFieldsValid: isValid,
    requiredSpecificFieldsErrors: errors,
  }
}
